import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { es as esLocale, enUS, pt as ptLocale, fr as frLocale } from "date-fns/locale";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Shield, CreditCard, Loader2, Wallet, Upload, Check, ShieldCheck, AlertCircle,
  MessageSquare, PhoneCall, Users, Download, LayoutGrid, CalendarDays, Clock, FileText,
} from "lucide-react";
import AppShell from "../components/app/AppShell";
import WhatsAppIcon from "../components/landing/WhatsAppIcon";
import { openWhatsApp } from "../utils/whatsappLinks";
import { WHATSAPP_NUMBER } from "../components/landing/constants";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import MercadoPagoFlow from "../components/payments/MercadoPagoFlow";
import CouponField from "../components/payments/CouponField";
import { usePaymentMethods } from "../hooks/usePaymentMethods";
import { apiErrorMessage } from "../utils/apiError";

/**
 * RC-WEB-6 PHASE 4/5/6 — the ONE canonical checkout.
 *
 * Two purchase contexts, one component, one route family:
 *   - `/checkout/:protectionId` → activate that protected profile (the profile
 *      NEVER comes from dashboard selection or other ambient state).
 *   - `/renovar`               → renew the account membership.
 *
 * Plans, prices, featured plan and payment rails all come from the canonical
 * backend. The previous `Realizar Pago` generation (PaymentUpload) is gone.
 */

const API = "/api";
const MAX_VOUCHER_BYTES = 8 * 1024 * 1024; // matches services/object_storage.validate_upload
const VOUCHER_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
const DATE_LOCALES = { es: esLocale, en: enUS, pt: ptLocale, fr: frLocale };

export default function Checkout() {
  const { protectionId } = useParams();
  const navigate = useNavigate();
  const isActivate = !!protectionId;

  const [protection, setProtection] = useState(null);
  const [protectionPayments, setProtectionPayments] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [culqiCfg, setCulqiCfg] = useState(null);
  const [paying, setPaying] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [payRef, setPayRef] = useState(null);
  const [uploadingVoucher, setUploadingVoucher] = useState(false);
  const [voucherUploaded, setVoucherUploaded] = useState(false);
  const [voucherFile, setVoucherFile] = useState(null);
  const voucherInputRef = useRef(null);
  const [showMP, setShowMP] = useState(false);
  const [rails, setRails] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const [upiQrUrl, setUpiQrUrl] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [protections, setProtections] = useState([]);
  const [searchParams] = useSearchParams();
  const [autoOpened, setAutoOpened] = useState(false);
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const cc = (user?.country_code || "").toUpperCase().slice(0, 2);
  const { isActive: methodActive, methodCopy } = usePaymentMethods(cc || "XX");
  const isPE = cc === "PE";
  const isIN = cc === "IN";
  const dateLocale = DATE_LOCALES[lang] || esLocale;

  const STATUS_CFG = {
    active: { label: t("app.membership.status_active"), cls: "border-emerald-300 bg-emerald-50 text-emerald-800" },
    expired: { label: t("app.membership.status_expired"), cls: "border-red-300 bg-red-50 text-red-800" },
    standby: { label: t("app.membership.status_standby"), cls: "border-slate-300 bg-slate-100 text-slate-700" },
    disabled: { label: t("app.membership.status_disabled"), cls: "border-red-300 bg-red-50 text-red-800" },
    pending_payment: { label: t("app.membership.status_pending"), cls: "border-amber-300 bg-amber-50 text-amber-900" },
  };

  const loadSub = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/subscription/me`);
      setSub(r.data);
    } catch (_) { /* generic UI below */ }
  }, []);

  // PHASE 5 — the billed profile is resolved from the ROUTE, never inferred.
  const loadProtection = useCallback(async () => {
    if (!protectionId) return;
    try {
      const [vr, pr] = await Promise.all([
        axios.get(`${API}/vehicles/${protectionId}`),
        axios.get(`${API}/payments/vehicle/${protectionId}`).catch(() => ({ data: [] })),
      ]);
      setProtection(vr.data);
      setProtectionPayments(Array.isArray(pr.data) ? pr.data : []);
      if (vr.data?.payment_status === "approved") {
        toast.info(t("app.checkout.already_active"));
        navigate(`/vehicle/${protectionId}`, { replace: true });
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, t("app.checkout.not_found")));
      navigate("/dashboard/vehicles", { replace: true });
    }
  }, [protectionId, navigate, t]);

  useEffect(() => {
    (async () => {
      await Promise.all([loadSub(), loadProtection()]);
      setLoading(false);
    })();
    (async () => {
      try {
        const r = await axios.get(`${API}/payments/culqi/config`);
        setCulqiCfg(r.data);
      } catch (_) { setCulqiCfg({ enabled: false }); }
    })();
    axios.get(`${API}/vehicles`).then((r) => setProtections(r.data || [])).catch(() => {});
  }, [loadSub, loadProtection]);

  useEffect(() => {
    axios.get(`${API}/membership/plans`, { params: { country_code: cc || undefined, locale: lang } })
      .then((r) => setCatalog(r.data))
      .catch(() => setCatalog(null));
  }, [cc, lang]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/payments/methods?country=${cc}`);
        setRails(r.data);
      } catch (_) { setRails({ methods: [] }); }
    })();
  }, [cc]);

  useEffect(() => {
    if (!culqiCfg?.enabled || window.Culqi) return;
    const s = document.createElement("script");
    s.src = "https://checkout.culqi.com/js/v4";
    s.async = true;
    s.onload = () => { if (window.Culqi) window.Culqi.publicKey = culqiCfg.public_key; };
    document.body.appendChild(s);
  }, [culqiCfg]);

  useEffect(() => {
    if (autoOpened || isActivate || searchParams.get("checkout") !== "1") return;
    if (culqiCfg?.enabled && window.Culqi) {
      setAutoOpened(true);
      openCulqiCheckout();
    }
  }, [culqiCfg, autoOpened]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentPlan = sub?.plan_months || sub?.months || 6;
  const [planMonths, setPlanMonths] = useState(null);
  const chosenMonths = planMonths || currentPlan;
  const pricing = sub?.pricing || null;
  const amountFor = (m) => (pricing ? (m === 12 ? pricing.amount_12m : pricing.amount_6m) : null);
  const fmtPrice = (m) => {
    const a = amountFor(m);
    return a == null ? null : `${pricing.symbol} ${Number(a).toLocaleString(lang)}`;
  };
  const penFor = (m) => (pricing ? `${pricing.pen_symbol} ${m === 12 ? pricing.pen_12m : pricing.pen_6m}` : null);
  const price = coupon
    ? `${coupon.display.symbol} ${Number(coupon.display.final).toLocaleString(lang)}`
    : fmtPrice(chosenMonths) || "…";
  const cfg = STATUS_CFG[sub?.status] || STATUS_CFG.pending_payment;
  const activeProtections = protections.filter((p) => p.qr_status === "active").length;
  const approximate = !!pricing && !pricing.own_price && pricing.display_currency !== pricing.charge_currency;

  const planCards = (catalog?.plans || [])
    .filter((p) => p.active && [6, 12].includes(p.duration_months))
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  const profilePending = protectionPayments.some((p) => p.status === "pending");
  const profileRejected = protectionPayments.find((p) => p.status === "rejected");

  useEffect(() => {
    let objUrl = null;
    (async () => {
      if (!isIN || !coupon) { setUpiQrUrl(null); return; }
      try {
        const r = await axios.get(`${API}/payments/upi-qr`, {
          params: { months: chosenMonths, coupon: coupon.code }, responseType: "blob" });
        objUrl = URL.createObjectURL(r.data);
        setUpiQrUrl(objUrl);
      } catch (_) { setUpiQrUrl(null); }
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIN, coupon, chosenMonths]);

  const chargeAmountStr = coupon
    ? `${coupon.charge.symbol} ${Number(coupon.charge.final).toLocaleString(lang)}`
    : (isIN ? fmtPrice(chosenMonths) : penFor(chosenMonths)) || "";
  const waText = (ref) =>
    t("checkout.wa_message", { amount: chargeAmountStr, email: sub?.email || user?.email || "", ref: ref || "" }) +
    (isActivate && protection?.name ? ` · ${t("app.checkout.ctx_activate", { name: protection.name })}` : "") +
    (coupon ? t("checkout.wa_coupon_part", { code: coupon.code, original: `${coupon.charge.symbol} ${Number(coupon.charge.original).toLocaleString(lang)}` }) : "");

  const openCulqiCheckout = () => {
    if (!window.Culqi || !culqiCfg?.enabled) return;
    setPaying(true);
    window.Culqi.publicKey = culqiCfg.public_key;
    window.Culqi.settings({ title: "AvisoCar", currency: "PEN", amount: culqiCfg.amount_cents });
    window.Culqi.options({
      lang: "es",
      paymentMethods: { tarjeta: true, yape: true, bancaMovil: false, agente: false, billetera: false, cuotealo: false },
    });
    window.culqi = async function () {
      if (window.Culqi.token) {
        try {
          const r = await axios.post(`${API}/payments/culqi/charge`, { token_id: window.Culqi.token.id });
          if (r.data?.ok) {
            toast.success(t("renew.card_confirmed"));
            await loadSub();
          }
        } catch (e) {
          toast.error(apiErrorMessage(e, t("app.inbox.action_error")), { duration: 9000 });
        }
      } else if (window.Culqi.error) {
        toast.error(window.Culqi.error.user_message || t("app.inbox.action_error"));
      }
      setPaying(false);
    };
    window.Culqi.open();
  };

  /* ------------------- voucher (both purchase contexts) ------------------- */

  const railMethodForUpload = () => {
    if (isIN) return "upi";
    if (isPE) return "yape_plin";
    return "transferencia";
  };

  const pickVoucher = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!VOUCHER_TYPES.includes(file.type)) {
      toast.error(t("app.checkout.voucher_type_error"));
      return;
    }
    if (file.size > MAX_VOUCHER_BYTES) {
      toast.error(t("app.checkout.voucher_size_error"));
      return;
    }
    setVoucherFile(file);
  };

  // Membership context keeps the historical subscription endpoints.
  const submitMembershipVoucher = async () => {
    if (!voucherFile) return;
    setUploadingVoucher(true);
    try {
      if (!payRef) {
        const r = await axios.post(`${API}/subscription/manual-request`,
          isIN ? { months: chosenMonths, method: "upi", coupon_code: coupon?.code || null }
               : { months: chosenMonths, coupon_code: coupon?.code || null });
        setPayRef(r.data.payment_ref);
      }
      const fd = new FormData();
      fd.append("voucher", voucherFile);
      await axios.post(`${API}/subscription/upload-voucher`, fd);
      setVoucherUploaded(true);
      setVoucherFile(null);
      toast.success(t("app.checkout.voucher_sent"));
      await loadSub();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("renew.voucher_error")));
    } finally {
      setUploadingVoucher(false);
    }
  };

  // Activation context posts against the profile from the ROUTE. The backend
  // rejects a second pending payment, so navigation cannot duplicate it.
  const submitProfileVoucher = async () => {
    if (!voucherFile || !protectionId) return;
    setUploadingVoucher(true);
    try {
      const fd = new FormData();
      fd.append("voucher", voucherFile);
      fd.append("method", railMethodForUpload());
      fd.append("plan_months", String(chosenMonths));
      if (coupon) fd.append("coupon_code", coupon.code);
      await axios.post(`${API}/payments/upload/${protectionId}`, fd);
      setVoucherUploaded(true);
      setVoucherFile(null);
      toast.success(t("app.checkout.voucher_sent"));
      navigate(`/vehicle/${protectionId}`, { replace: true });
    } catch (e) {
      toast.error(apiErrorMessage(e, t("renew.voucher_error")));
    } finally {
      setUploadingVoucher(false);
    }
  };

  const submitVoucher = isActivate ? submitProfileVoucher : submitMembershipVoucher;

  const requestManual = async () => {
    setRequesting(true);
    try {
      const r = await axios.post(`${API}/subscription/manual-request`,
        isIN ? { months: chosenMonths, method: "upi", coupon_code: coupon?.code || null }
             : { months: chosenMonths, coupon_code: coupon?.code || null });
      const ref = r.data?.payment_ref;
      setPayRef(ref || null);
      toast.success(r.data?.message || t("renew.manual_requested"), { duration: 8000 });
      await loadSub();
      openWhatsApp(WHATSAPP_NUMBER, waText(ref));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("app.inbox.action_error")));
    } finally {
      setRequesting(false);
    }
  };

  /* ---------------------------- hero ---------------------------- */

  const statusTile = (icon, label, value, testid) => {
    const Icon = icon;
    return (
      <div className="rounded-xl border border-white/15 bg-white/10 p-3" data-testid={testid}>
        <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-300">
          <Icon className="h-3.5 w-3.5" /> {label}
        </p>
        <p className="mt-1 text-base font-bold text-white">{value}</p>
      </div>
    );
  };

  const membershipHero = (
    <section className="overflow-hidden rounded-3xl bg-[#0B2545] p-6 sm:p-8" data-testid="membership-hero">
      <div className="flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#1B84FF]/25 text-[#5BC0FF]">
          <ShieldCheck className="h-6 w-6" />
        </span>
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-white sm:text-2xl" style={{ fontFamily: "Manrope, sans-serif" }}>
            {t("app.membership.hero_title")}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-300">{t("app.membership.hero_text")}</p>
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="membership-status-grid">
        <div className="rounded-xl border border-white/15 bg-white/10 p-3" data-testid="membership-status-tile">
          <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-300">
            <Shield className="h-3.5 w-3.5" /> {t("app.membership.status")}
          </p>
          <p className="mt-1">
            <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-bold ${cfg.cls}`} data-testid="renew-status-badge">
              {cfg.label}{sub?.in_grace ? ` ${t("app.membership.grace")}` : ""}
            </span>
          </p>
        </div>
        {statusTile(LayoutGrid, t("app.membership.protected_count"), activeProtections, "membership-protections-tile")}
        {statusTile(CalendarDays, t("app.membership.expires"),
          sub?.expires_at ? format(new Date(sub.expires_at), "d MMM yyyy", { locale: dateLocale }) : "—",
          "membership-expiry-tile")}
        {statusTile(Clock, t("app.membership.days_left"),
          t("app.membership.days_value", { n: sub?.days_remaining ?? 0 }), "membership-days-tile")}
      </div>

      {sub?.has_pending_request && (
        <p className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900" data-testid="renew-pending-request">
          {t("app.membership.pending_request")}
        </p>
      )}
    </section>
  );

  const activateHero = (
    <section className="overflow-hidden rounded-3xl bg-[#0B2545] p-6 sm:p-8" data-testid="checkout-activate-hero">
      <div className="flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#1B84FF]/25 text-[#5BC0FF]">
          <ShieldCheck className="h-6 w-6" />
        </span>
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-white sm:text-2xl" style={{ fontFamily: "Manrope, sans-serif" }}>
            {t("app.checkout.hero_activate_title", { name: protection?.name || "" })}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-slate-300">{t("app.checkout.hero_activate_text")}</p>
        </div>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-white/15 bg-white/10 p-3" data-testid="checkout-profile-tile">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-300">{t("app.checkout.what_you_pay")}</p>
          <p className="mt-1 truncate text-base font-bold text-white" data-testid="checkout-profile-name">{protection?.name || "—"}</p>
        </div>
        <div className="rounded-xl border border-white/15 bg-white/10 p-3" data-testid="checkout-profile-status">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-300">{t("app.membership.status")}</p>
          <p className="mt-1">
            <span className="inline-block rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-xs font-bold text-amber-900">
              {profilePending ? t("app.protections.status_review") : t("app.protections.status_pending")}
            </span>
          </p>
        </div>
        {statusTile(LayoutGrid, t("app.membership.protected_count"), activeProtections, "checkout-active-count")}
      </div>
      {profilePending && (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5" data-testid="checkout-review-banner">
          <p className="text-sm font-bold text-amber-900">{t("app.checkout.review_title")}</p>
          <p className="text-sm text-amber-900">{t("app.checkout.review_text")}</p>
        </div>
      )}
      {!profilePending && profileRejected && (
        <div className="mt-4 rounded-xl border border-red-300 bg-red-50 px-3 py-2.5" data-testid="checkout-rejected-banner">
          <p className="flex items-center gap-1.5 text-sm font-bold text-red-800">
            <AlertCircle className="h-4 w-4" /> {t("app.checkout.rejected_title")}
          </p>
          <p className="text-sm text-red-700">{profileRejected.rejection_reason || profileRejected.admin_notes || ""}</p>
        </div>
      )}
    </section>
  );

  /* ---------------------------- plan cards ---------------------------- */

  const planSection = (
    <section data-testid="renew-plan-selector">
      <h2 className="mb-1 text-lg font-bold text-[#0B2545]">{t("app.membership.choose_plan")}</h2>
      {!isActivate && (
        <p className="mb-4 text-sm text-slate-600">
          {t("app.membership.current_plan")}:{" "}
          <span className="font-semibold text-slate-800" data-testid="renew-current-plan">
            {currentPlan === 12 ? t("app.membership.plan_12m") : t("app.membership.plan_6m")}
          </span>
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {(planCards.length ? planCards : [{ duration_months: 6 }, { duration_months: 12 }]).map((p) => {
          const m = p.duration_months;
          const selected = chosenMonths === m;
          const featured = !!(p.featured || p.is_default);
          const label = p.names?.[lang] || p.name || (m === 12 ? t("app.membership.plan_12m") : t("app.membership.plan_6m"));
          return (
            <button
              key={m}
              type="button"
              onClick={() => setPlanMonths(m)}
              disabled={!pricing}
              data-testid={`renew-plan-${m}`}
              aria-pressed={selected}
              className={`relative flex flex-col items-start rounded-2xl border-2 bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                selected ? "border-[#1B84FF] shadow-md shadow-[#1B84FF]/15" : "border-slate-200"
              }`}
            >
              {featured && (
                <span className="absolute right-4 top-4 rounded-full bg-[#1B84FF] px-2.5 py-0.5 text-[11px] font-bold text-white"
                      data-testid={`renew-plan-${m}-featured`}>
                  {t("app.membership.recommended")}
                </span>
              )}
              <span className="text-sm font-semibold text-slate-700">{label}</span>
              <span className="mt-2 text-3xl font-extrabold text-[#0B2545]" data-testid={`renew-plan-${m}-price`}>
                {approximate ? "≈ " : ""}{fmtPrice(m) || "…"}
              </span>
              <span className="mt-1 text-xs text-slate-600">
                {t("app.membership.per_months", { n: m })}
              </span>
              {approximate && (
                <span className="mt-2 text-[11px] font-medium text-slate-700" data-testid={`renew-plan-${m}-charge`}>
                  {t("app.membership.charge_note", { pen: penFor(m) })}
                </span>
              )}
              <span className={`mt-4 flex items-center gap-1.5 text-xs font-bold ${selected ? "text-[#1B84FF]" : "text-slate-500"}`}>
                {selected ? <><Check className="h-4 w-4" /> {t("app.membership.selected")}</> : t("app.membership.select")}
              </span>
            </button>
          );
        })}
      </div>
      {approximate && (
        <p className="mt-3 text-xs font-medium text-slate-700" data-testid="renew-charged-pen">
          {t("app.membership.estimate_note")}
        </p>
      )}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="renew-coupon-field">
        <CouponField months={chosenMonths} onChange={setCoupon} />
      </div>
    </section>
  );

  const benefits = [
    [Shield, t("app.membership.benefit_qr")],
    [MessageSquare, t("app.membership.benefit_messages")],
    [PhoneCall, t("app.membership.benefit_calls")],
    [Users, t("app.membership.benefit_trusted")],
    [Download, t("app.membership.benefit_sticker")],
    [LayoutGrid, t("app.membership.benefit_manage")],
  ];

  const summary = (
    <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="renew-order-summary">
      <h2 className="mb-4 text-base font-bold text-[#0B2545]">{t("app.membership.summary_title")}</h2>
      <dl className="space-y-2.5 text-sm">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-slate-700">{t("app.checkout.what_you_pay")}</dt>
          <dd className="max-w-[60%] text-right font-semibold text-[#0B2545]" data-testid="checkout-context-value">
            {isActivate ? t("app.checkout.ctx_activate", { name: protection?.name || "" }) : t("app.checkout.ctx_renew")}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-slate-700">{t("app.membership.summary_plan")}</dt>
          <dd className="font-semibold text-[#0B2545]" data-testid="summary-plan-value">
            {chosenMonths === 12 ? t("app.membership.plan_12m") : t("app.membership.plan_6m")}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3 border-t border-slate-200 pt-2.5">
          <dt className="text-slate-700">{t("app.membership.summary_amount")}</dt>
          <dd className="text-xl font-extrabold text-[#0B2545]" data-testid="summary-amount-value">
            {approximate ? "≈ " : ""}{price}
          </dd>
        </div>
        {approximate && (
          <p className="text-xs font-medium text-slate-700" data-testid="summary-charge-note">
            {t("app.membership.charge_note", { pen: coupon ? `S/ ${Number(coupon.charge.final)}` : penFor(chosenMonths) })}
          </p>
        )}
      </dl>
      <div className="mt-4 rounded-xl border border-[#1B84FF]/30 bg-[#1B84FF]/5 p-3">
        <p className="text-xs font-bold uppercase tracking-wide text-[#0F6FDB]">{t("app.membership.summary_after")}</p>
        <p className="mt-1 text-sm text-slate-700">{t("app.membership.summary_after_text")}</p>
      </div>
      <p className="mt-3 text-xs text-slate-600" data-testid="checkout-entitlement-note">{t("app.checkout.entitlement_note")}</p>
      <p className="mt-2 text-xs text-slate-600" data-testid="checkout-qr-note">{t("app.checkout.qr_note")}</p>
      <ul className="mt-5 space-y-2 border-t border-slate-200 pt-4">
        <li className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-700">{t("app.membership.benefits_title")}</li>
        {benefits.map(([Icon, text]) => (
          <li key={text} className="flex items-start gap-2 text-sm text-slate-700">
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#1B84FF]" /> {text}
          </li>
        ))}
      </ul>
    </section>
  );

  /* ---------------------------- payment rails ---------------------------- */

  const railShell = (title, icon, iconCls, children, testid) => {
    const Icon = icon;
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
        <div className="mb-2 flex items-center gap-3">
          <Icon className={`h-5 w-5 ${iconCls}`} />
          <h3 className="font-semibold text-[#0B2545]">{title}</h3>
        </div>
        {children}
      </div>
    );
  };

  const voucherBlock = (
    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="checkout-voucher-block">
      <p className="text-sm font-bold text-[#0B2545]">{t("app.checkout.voucher_title")}</p>
      <p className="mt-0.5 text-xs text-slate-600">{t("app.checkout.voucher_text")}</p>
      <input type="file" accept="image/png,image/jpeg,application/pdf" ref={voucherInputRef} className="hidden"
             onChange={pickVoucher} data-testid="renew-voucher-input" />
      <Button onClick={() => voucherInputRef.current?.click()} variant="outline"
              className="mt-3 w-full rounded-full border-slate-300 font-bold text-[#0B2545]"
              data-testid="checkout-pick-voucher-btn">
        <FileText className="mr-2 h-4 w-4" />
        {voucherFile ? t("app.checkout.voucher_change") : t("app.checkout.voucher_select")}
      </Button>
      {voucherFile && (
        <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="checkout-voucher-selected">
          <Check className="h-3.5 w-3.5" /> {t("app.checkout.voucher_selected")}: {voucherFile.name}
        </p>
      )}
      <p className="mt-1.5 text-[11px] text-slate-500" data-testid="checkout-voucher-limit">{t("app.checkout.voucher_limit")}</p>
      <Button onClick={submitVoucher} disabled={!voucherFile || uploadingVoucher}
              className="mt-3 w-full rounded-full bg-emerald-600 font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
              data-testid="renew-upload-voucher-btn">
        {uploadingVoucher ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
        {uploadingVoucher ? t("app.checkout.voucher_submitting")
          : voucherUploaded ? t("renew.voucher_uploaded") : t("app.checkout.voucher_submit")}
      </Button>
    </div>
  );

  const cardOption = railShell(t("renew.card_title"), CreditCard, "text-[#1B84FF]", (
    <>
      <p className="mb-3 text-sm text-slate-700">{t("renew.card_text", { price })}</p>
      <Button onClick={openCulqiCheckout} disabled={paying}
              className="w-full rounded-full bg-[#1B84FF] font-bold text-white hover:bg-[#0F6FDB]"
              data-testid="renew-card-btn">
        {paying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
        {t("renew.card_btn", { price })}
      </Button>
      {methodCopy("culqi")?.confirmation_eta && (
        <p className="mt-2 text-xs text-slate-700" data-testid="renew-card-eta">{methodCopy("culqi").confirmation_eta}</p>
      )}
    </>
  ), "renew-card-option");

  const mpOption = railShell(t("renew.mp_title"), Wallet, "text-[#009EE3]", (
    <>
      <p className="mb-3 text-sm text-slate-700">
        {methodCopy("mercado_pago")?.instructions || t("renew.mp_text")}
      </p>
      {!isPE && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs font-medium text-amber-900" data-testid="renew-mp-pen-note">
          {t("mp.pen_note")}
        </div>
      )}
      {showMP ? (
        <MercadoPagoFlow showPenNote={false} months={chosenMonths} />
      ) : (
        <Button onClick={() => setShowMP(true)}
                className="w-full rounded-full bg-[#009EE3] font-bold text-white hover:bg-[#0087C7]"
                data-testid="renew-mp-btn">
          <Wallet className="mr-2 h-4 w-4" /> {t("renew.mp_btn")}
        </Button>
      )}
      {methodCopy("mercado_pago")?.confirmation_eta && (
        <p className="mt-2 text-xs text-slate-700" data-testid="renew-mp-eta">{methodCopy("mercado_pago").confirmation_eta}</p>
      )}
    </>
  ), "renew-mp-option");

  const manualOption = railShell(t("renew.manual_title"), WhatsAppIcon, "text-[#25D366]", (
    <>
      <p className="mb-3 text-sm text-slate-700">
        {(isIN ? methodCopy("upi")?.instructions : methodCopy(isPE ? "yape_plin" : "transferencia")?.instructions)
          || (isPE ? t("renew.manual_text") : isIN ? t("checkout.upi_text") : t("renew.manual_intl_text"))}
      </p>
      {isIN && rails?.upi && (
        <div className="mb-3 flex flex-col items-center gap-4 rounded-xl border border-slate-200 p-4 sm:flex-row" data-testid="renew-upi-info">
          <img src={upiQrUrl || rails.upi.qr_image_url} alt="UPI QR"
               className="h-auto w-40 rounded-lg border border-slate-200" data-testid="renew-upi-qr-image" />
          <div className="text-center text-sm text-slate-700 sm:text-left">
            <p><span className="font-semibold">{t("checkout.upi_holder_label")}:</span> {rails.upi.holder}</p>
            <p><span className="font-semibold">{t("checkout.upi_vpa_label")}:</span> <span className="font-mono" data-testid="renew-upi-vpa">{rails.upi.vpa}</span></p>
            <p className="mt-2 text-2xl font-extrabold text-slate-900" data-testid="renew-upi-amount">
              {coupon ? (
                <>
                  <span className="mr-2 text-base text-slate-600 line-through">{fmtPrice(chosenMonths)}</span>
                  {coupon.display.symbol} {Number(coupon.display.final).toLocaleString("en-IN")}
                </>
              ) : fmtPrice(chosenMonths) || "…"}
            </p>
            <p className="mt-1 text-xs text-slate-700">{t("checkout.upi_honest")}</p>
          </div>
        </div>
      )}
      {isPE && rails?.methods?.includes("yape") && (
        <div className="mb-3 flex flex-col items-center gap-4 rounded-xl border border-slate-200 p-4 sm:flex-row" data-testid="checkout-yape-info">
          <img src={`${API}/payment-config/qr/unified`} alt="Yape / Plin QR"
               className="h-auto w-40 rounded-lg border border-slate-200" data-testid="checkout-yape-qr" />
          <div className="text-center text-sm text-slate-700 sm:text-left">
            <p className="text-2xl font-extrabold text-slate-900" data-testid="checkout-yape-amount">
              {penFor(chosenMonths) || "…"}
            </p>
            <p className="mt-1 text-xs text-slate-600">{t("app.membership.charge_note", { pen: penFor(chosenMonths) })}</p>
          </div>
        </div>
      )}
      {payRef && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="renew-payment-ref">
          <span className="text-amber-900">{t("app.checkout.ref_label")} </span>
          <span className="font-mono font-bold text-[#0B2545]">{payRef}</span>
        </div>
      )}
      {voucherBlock}
      <Button onClick={requestManual} disabled={requesting} variant="outline"
              className="mt-3 w-full rounded-full border-[#25D366] font-bold text-emerald-800 hover:bg-emerald-50"
              data-testid="renew-manual-btn">
        {requesting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <WhatsAppIcon className="mr-2 h-4 w-4" />}
        {t("renew.manual_btn")}
      </Button>
      <p className="mt-2 text-xs text-slate-600" data-testid="checkout-wa-note">{t("app.checkout.wa_note")}</p>
      <p className="mt-2 text-center text-xs text-slate-700" data-testid="renew-manual-eta">
        {(isIN ? methodCopy("upi")?.confirmation_eta : methodCopy(isPE ? "yape_plin" : "transferencia")?.confirmation_eta) || ""}
      </p>
    </>
  ), "renew-manual-option");

  const couponNote = (key, testid) => (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm font-medium text-amber-900" data-testid={testid}>
      {t(key)}
    </div>
  );

  const paymentSection = (
    <section data-testid="renew-payment-section">
      <h2 className="mb-4 text-lg font-bold text-[#0B2545]">{t("app.membership.pay_title")}</h2>
      <div className="space-y-4">
        {!rails ? (
          <div className="flex items-center gap-2 text-sm text-slate-700" data-testid="renew-rails-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("app.loading")}
          </div>
        ) : isPE ? (
          <>
            {!isActivate && (coupon ? couponNote("checkout.coupon_no_card", "coupon-rail-note-card")
              : (methodActive("culqi") && culqiCfg?.enabled ? cardOption : null))}
            {!isActivate && methodActive("mercado_pago") && rails.methods?.includes("mp") && (coupon
              ? couponNote("checkout.coupon_no_mp_pe", "coupon-rail-note-mp") : mpOption)}
            {manualOption}
          </>
        ) : (
          <>
            {manualOption}
            {!isActivate && methodActive("mercado_pago") && rails.methods?.includes("mp") && (coupon
              ? couponNote("checkout.coupon_no_mp_intl", "coupon-rail-note-mp") : mpOption)}
          </>
        )}
        {rails && rails.methods?.length === 0 && (
          <div className="rounded-2xl border border-slate-300 bg-white p-5 text-sm text-slate-700" data-testid="renew-no-methods">
            {t("app.membership.pay_none")}{" "}
            <Link to="/contacto" className="font-semibold text-[#1B84FF] hover:underline">{t("app.membership.history")}</Link>
          </div>
        )}
      </div>
    </section>
  );

  return (
    <AppShell
      title={isActivate ? t("app.checkout.title_activate") : t("app.checkout.title_renew")}
      subtitle={isActivate ? t("app.checkout.subtitle_activate") : t("app.checkout.subtitle_renew")}
      maxWidth="max-w-6xl"
      backTo={isActivate ? `/vehicle/${protectionId}` : "/dashboard"}
    >
      <div className="space-y-6" data-testid="renew-page">
        {loading ? (
          <div className="flex items-center gap-2 py-16 text-sm text-slate-700" data-testid="renew-loading">
            <Loader2 className="h-5 w-5 animate-spin text-[#1B84FF]" /> {t("app.loading")}
          </div>
        ) : (
          <>
            {isActivate ? activateHero : membershipHero}
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
              <div className="space-y-6">
                {planSection}
                {paymentSection}
              </div>
              <div className="space-y-6 lg:sticky lg:top-24 lg:self-start">{summary}</div>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
